import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Modal, Button, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FormikMaterialUiTimePicker } from '../../components';
import { delayMethod } from '../../services/utils';
import useTimeCalculation from '../../../hooks/useTimeCalculation';

const DateForm = ({
  cancelRequest,
  confirmRequest,
  date,
  defAgreedSchedule,
  defEndTime,
  defStartTime,
  defStartLunch,
  defInitialTime,
  defEndLunch,
  defFlexibleShift,
  defWorkHours,
  defLunchTime,
  defImputableColation,
  destroy,
  destroyRequest = null,
  id,
  oldTitle,
  defIsNightShift,
  defTelecommuting
}) => {
  const [startTime, setStartTime] = useState(defStartTime);
  const [endTime, setEndTime] = useState(defEndTime);
  const [startLunch, setStartLunch] = useState(defAgreedSchedule ? defStartLunch : '');
  const [endLunch, setEndLunch] = useState(defAgreedSchedule ? defEndLunch : '');
  const [isNightShift, setIsNightShift] = useState(defIsNightShift);
  const [telecommuting, setTelecommuting] = useState(defTelecommuting);
  const { advanceSettings } = useSelector(state => state.utils);
  const nightShiftRange = advanceSettings
    .filter(asItem => asItem.code === 'night_shift_range')
    .some(x => x.textValue === 'true');
  const disabled = startTime === endTime;
  const { calculateEndTime } = useTimeCalculation();
  const memoizedCalculateEndTime = useCallback(calculateEndTime, []);
  const params = {
    date,
    startTime,
    endTime,
    startLunch,
    endLunch,
    id,
    oldTitle,
    title: `${startTime} - ${endTime} ${defAgreedSchedule ? `/ ${startLunch} - ${endLunch} ` : ''}`,
    isNightShift,
    telecommuting
  };
  const [error, setError] = useState('');
  const [errorLunch, setErrorLunch] = useState('');

  const checkFlexibleShift = () => {
    if (defFlexibleShift) {
      const endTimeWork = memoizedCalculateEndTime(
        defInitialTime,
        defWorkHours,
        defLunchTime || '0',
        defImputableColation
      );
      setStartTime(defInitialTime);
      setEndTime(endTimeWork);
    }
  };

  const checkStartTime = currentTime => {
    if (currentTime === endTime) {
      setError('Hora de inicio no puede ser igual que la de termino');
      return setStartTime(currentTime);
    }
    setError('');
    return setStartTime(currentTime);
  };
  const checkEndtime = currentTime => {
    if (currentTime === startTime) {
      setError('Hora de termino no puede ser igual que la de inicio');
      return setEndTime(currentTime);
    }
    setError('');
    return setEndTime(currentTime);
  };

  const checkStartLunch = currentTime => {
    if (currentTime > endLunch) {
      setErrorLunch('Hora de inicio de colación no puede ser mayor que la de termino');
      return setStartLunch(currentTime);
    }
    setErrorLunch('');
    return setStartLunch(currentTime);
  };
  const checkEndLunch = currentTime => {
    if (currentTime < startLunch) {
      setErrorLunch('Hora de termino de colación no puede ser menor que la de inicio');
      return setEndLunch(currentTime);
    }
    setErrorLunch('');
    return setEndLunch(currentTime);
  };

  useEffect(checkFlexibleShift, []);

  return (
    <>
      <Modal.Body>
        <Row>
          <Col xs={12} md={6}>
            <FormikMaterialUiTimePicker
              abbr
              label="Desde"
              timeSelector
              defaultValue={startTime}
              onChange={checkStartTime}
              disabled={defFlexibleShift}
            />
            <p className="text-danger">{error}</p>
          </Col>
          <Col xs={12} md={6}>
            <FormikMaterialUiTimePicker
              abbr
              label="Hasta"
              timeSelector
              defaultValue={endTime}
              onChange={checkEndtime}
              disabled={defFlexibleShift}
            />
          </Col>
        </Row>
        {defAgreedSchedule && (
          <Row>
            <Col xs={12} md={6}>
              <FormikMaterialUiTimePicker
                abbr
                label="Desde"
                timeSelector
                defaultValue={startLunch}
                onChange={checkStartLunch}
              />
              <p className="text-danger">{errorLunch}</p>
            </Col>
            <Col xs={12} md={6}>
              <FormikMaterialUiTimePicker
                abbr
                label="Hasta"
                timeSelector
                defaultValue={endLunch}
                onChange={checkEndLunch}
              />
            </Col>
          </Row>
        )}
        {nightShiftRange && (
          <Row>
            <Col xs={12} md={12} className="mt-3">
              <Form.Check
                type="checkbox"
                label="Turno nocturno (Resumen de turno)"
                checked={isNightShift}
                onChange={e => setIsNightShift(e.target.checked)}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12} md={12} className="mt-2">
            <Form.Check
              type="checkbox"
              label="Teletrabajo"
              checked={telecommuting}
              onChange={e => setTelecommuting(e.target.checked)}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button className="text-center" variant="outline-info" onClick={() => delayMethod(cancelRequest)}>
          Cancelar
        </Button>
        {destroy && (
          <Button className="text-center" variant="danger" onClick={() => destroyRequest(params)}>
            Eliminar
          </Button>
        )}
        <Button type="submit" disabled={disabled} variant="primary" onClick={() => confirmRequest(params)}>
          Agregar
        </Button>
      </Modal.Footer>
    </>
  );
};

DateForm.propTypes = {
  confirmRequest: PropTypes.func.isRequired,
  cancelRequest: PropTypes.func.isRequired,
  defStartTime: PropTypes.string,
  defEndTime: PropTypes.string,
  defStartLunch: PropTypes.string,
  defEndLunch: PropTypes.string,
  destroy: PropTypes.bool,
  oldTitle: PropTypes.string,
  defAgreedSchedule: PropTypes.bool,
  defFlexibleShift: PropTypes.bool,
  defImputableColation: PropTypes.bool,
  defIsNightShift: PropTypes.bool,
  defTelecommuting: PropTypes.bool
};

DateForm.defaultProps = {
  defAgreedSchedule: false,
  defStartTime: '09:00',
  defEndTime: '18:00',
  defStartLunch: '14:00',
  defEndLunch: '15:00',
  destroy: false,
  oldTitle: '',
  defImputableColation: false,
  defFlexibleShift: false,
  defIsNightShift: false,
  defTelecommuting: false
};

export default DateForm;
